import styled from "@emotion/styled";
import React from "react";

import useLazyLoadCheck from "@brexhq/next/src/hooks/useLazyLoadCheck";
import { IBynderImage } from "@brexhq/shared/types/BynderAsset";
import isChromatic from "@brexhq/shared/utils/env/isChromatic";

const getHeightFromImage = (width: number, aspectRatio: number) =>
  Math.max(Math.round(width * aspectRatio), 0);

export const getWidthFromImage = (height: number, aspectRatio: number) =>
  Math.max(Math.round(height / aspectRatio), 5);

const Image = styled("img")({
  maxWidth: "100%",
  color: "transparent", // hide alt text while image is loading but keep it readable by screen reader
});

const generateSrcSet = (asset: IBynderImage, maxWidth: number) => {
  const srcset: string[] = [];
  for (let i = maxWidth; i > 300; i -= 150) {
    const url = buildBynderImageUrl(asset, { width: i });
    const src = `${url} ${i}w`;
    srcset.push(src);
  }
  return srcset.toString();
};

/**
 * @deprecated Prefer to use NextImage, which calculates this srcSet automatically.
 */
export const buildBynderImageUrl = (
  image: IBynderImage | null | undefined,
  options: { width: number },
) => {
  if (!image) return undefined;
  const { aspectRatio, datUrl, previewImg } = image;

  const imageUrl = datUrl ?? previewImg;
  if (!imageUrl) return undefined;

  const width = options.width;
  // Set 3 as a reasonable minimum height -- Bynder will give up if the aspect ratio is too crazy.
  const height = getHeightFromImage(width, aspectRatio);

  return (
    imageUrl + `?io=transform:fit,width:${width},height:${height}&quality=95`
  );
};

export type LegacyImageProps = {
  image: IBynderImage;
  maxWidth?: number;
  altTextOverride?: string;
  className?: string;
  loading?: "eager" | "lazy";
};

/**
 * @deprecated Prefer to use NextImage.
 */
export const LegacyImage: React.FC<LegacyImageProps> = ({
  image,
  maxWidth = 2000,
  altTextOverride,
  className,
  loading,
}) => {
  const shouldLazyLoadByPosition = useLazyLoadCheck();
  const shouldLazyLoad =
    loading === "eager"
      ? false
      : loading === "lazy"
      ? true
      : shouldLazyLoadByPosition;
  if (!image) return null;
  const srcSets = generateSrcSet(image, maxWidth);
  const altText = altTextOverride ?? image?.description ?? image?.name;

  return (
    <Image
      data-testid="legacy-image"
      loading={shouldLazyLoad ? "lazy" : "eager"}
      sizes={`(max-width: ${maxWidth}px) 100vw, ${maxWidth}px`}
      src={buildBynderImageUrl(image, { width: maxWidth })}
      srcSet={srcSets}
      alt={altText ?? image.description ?? image.name}
      className={className}
      data-chromatic={isChromatic() ? "ignore" : undefined}
    />
  );
};
